import DocumentUtils from 'services/DocumentUtils';
import React, { useState, useMemo } from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import DocumentTemplateList from 'components/HealthRecords/Documents/DocumentTemplateList';
import PrefilledTemplates from 'components/HealthRecords/Documents/DocumentTemplate/PrefilledTemplates';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { uiActions } from 'careand-redux/slices/ui/ui';
import DocumentTemplateUtils from 'services/DocumentTemplateUtils';
import { useAppSelector } from 'careand-redux/store';
import { GetStaff } from 'careand-redux/selectors';
import { PhysicianPreferencesTemplateOpenModeEnum } from 'generated/api/models/PhysicianPreferences';
import _ from 'lodash';
import calendarUtils from 'services/CalendarUtils';
import UnreadBadge from 'components/Common/UnreadBadge';

export const CATEGORIES = [
  {
    name: 'Recent',
    showRecent: true,
    className: ' secondary-blue',
    documentType: undefined,
  },
  {
    name: 'Reqs',
    showCategories: ['Requisitions'],
    showCategoryTitle: false,
    className: ' secondary-blue',
    documentType: DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition,
  },
  {
    name: 'Pre-filled Reqs',
    prefilled: true,
    documentType: DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition,
  },
  {
    name: 'Referrals',
    showCategories: ['Referrals'],
    showCategoryTitle: false,
    className: ' secondary-blue',
    documentType: DocumentUtils.DOCUMENT_TYPE.referral,
  },
  {
    name: 'Resources',
    hideCategories: ['Requisitions', 'Referrals', 'Anatomical Images'],
    showCategoryTitle: true,
    documentType: undefined,
  },
];

const SECONDARY_NAMES_ORDER = [
  'Laboratory > MOHLTC',
  'Laboratory > Public Health Ontario',
  'Laboratory > Dynacare',
  'Laboratory > LifeLabs',
  'Laboratory',
  'Imaging > Toronto',
  'Imaging > Toronto & GTA',
  'Imaging > GTA',
  'Imaging > Hospitals',
  'Imaging',
];

const DocumentTemplatesSection = ({ patient, physician }) => {
  const dispatch = useAppDispatch();
  const templateCategories = useAppSelector((state) => state.templates.templateCategories);
  const templates = useAppSelector((state) => state.templates.templates);
  const allDocuments = useAppSelector((state) => state.healthRecords.byId[patient.id]?.documents);

  const ownUser = useAppSelector(GetStaff);
  const openMode =
    ownUser?.preferences?.template_open_mode || PhysicianPreferencesTemplateOpenModeEnum.Pin;
  const shouldPin = openMode === PhysicianPreferencesTemplateOpenModeEnum.Pin;

  const [categoryIndex, setCategoryIndex] = useState(1);

  const usedTemplates = useMemo(() => {
    let result = {};
    allDocuments.forEach((document) => {
      if (document.template_id) {
        let template = templates.find((t) => t.id === document.template_id);
        if (template) {
          result[document.template_id] = result[document.template_id] || {
            count: 0,
            template,
            date: document.date,
          };
          result[document.template_id].count++;
          result[document.template_id].date = Math.max(
            result[document.template_id].date,
            document.date,
          );
        }
      }
    });
    return _.sortBy(Object.values(result), 'count').reverse();
  }, [allDocuments, templates]);

  const tabs = useMemo(() => {
    return CATEGORIES.map((category) => {
      if (category.showRecent)
        return {
          ...category,
          hide: usedTemplates.length < 0,
          count: usedTemplates.length,
        };
      return category;
    });
  }, [usedTemplates]);

  function onPin(template) {
    let defaultSavedDocumentCategory = DocumentUtils.getTemplateMatchingDocumentCategory(
      template,
      templateCategories,
    );

    dispatch(
      uiActions.addPinnedDocument({
        patientId: patient.id,
        type: 'template',
        id: template.id,
        title: template.description,
        payload: {
          defaultSavedDocumentCategory,
          physician,
        },
      }),
    );
  }

  function onClickPrefilled(prefilledTemplate, index = 0) {
    dispatch(
      uiActions.addPinnedDocument({
        patientId: patient.id,
        type: 'template',
        tabTitle: prefilledTemplate.title,
        payload: {
          overrideId: prefilledTemplate.template_id + index,
          templateId: prefilledTemplate.template_id,
          prefilledValues: DocumentTemplateUtils.convertPrefilledValues(prefilledTemplate.values),
          defaultSavedDocumentCategory: CATEGORIES[categoryIndex].documentType,
          physician,
        },
      }),
    );
  }

  function getRecentDescription(template) {
    let found = usedTemplates.find((recent) => recent.template?.id === template.id);
    if (found) {
      return `${template.description} (${found.count} use${
        found.count > 1 ? 's' : ''
      }, ${calendarUtils.fromNow(found.date)})`;
    }
  }

  return (
    <div className="new-diagnostic">
      <div className="tabs">
        <Tabs
          value={categoryIndex}
          indicatorColor="primary"
          textColor="primary"
          onChange={(event, index) => setCategoryIndex(index)}
        >
          {tabs.map(({ name, hide, count }, index) => {
            return (
              <Tab
                style={hide ? { display: 'none' } : undefined}
                key={name}
                label={
                  <span>
                    {name}
                    {count > 0 && (
                      <UnreadBadge
                        unreadCount={count}
                        style={{
                          background: categoryIndex === index ? '#2693d1' : '#B9B9B9',
                          color: 'white',
                        }}
                      />
                    )}
                  </span>
                }
              />
            );
          })}
        </Tabs>
      </div>
      <div className="row details-panel scroll-list">
        {CATEGORIES[categoryIndex]?.prefilled ? (
          <PrefilledTemplates
            isOwn
            patient={patient}
            hideTitle={true}
            defaultSavedDocumentCategory={CATEGORIES[categoryIndex].documentType}
            physician={physician}
            onClick={shouldPin ? onClickPrefilled : undefined}
            showOpenMode={true}
          />
        ) : (
          <DocumentTemplateList
            key={categoryIndex}
            patient={patient}
            physician={physician}
            startExpanded={true}
            hideAddButton
            showCategories={CATEGORIES[categoryIndex].showCategories}
            hideCategories={CATEGORIES[categoryIndex].hideCategories}
            className={CATEGORIES[categoryIndex].className}
            showSecondaryName={true}
            ids={
              CATEGORIES[categoryIndex].showRecent
                ? usedTemplates.map((t) => t.template?.id)
                : undefined
            }
            hideTitle={true}
            secondaryNamesOrder={SECONDARY_NAMES_ORDER}
            onClick={shouldPin ? onPin : undefined}
            searchAll={true}
            showOpenMode={true}
            getDescription={CATEGORIES[categoryIndex].showRecent ? getRecentDescription : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentTemplatesSection;
